import type { InvoiceDetail } from '../useQueryInvoiceDetail/types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysInvoice } from '../queryKeys';

import {
  type UpdateLineItemApprovedMutationOptions,
  type UpdateLineItemApprovedVariables,
} from './types';
import { updateLineItemApproved } from './updateLineItemApproved';

function getOptimisticLineItem(
  lineItem: InvoiceDetail['lineItems'][number],
  payload: UpdateLineItemApprovedVariables['payload'],
) {
  return {
    ...lineItem,
    quantityApproved:
      payload.quantityApproved === undefined
        ? lineItem.quantityApproved
        : payload.quantityApproved == null
          ? null
          : String(payload.quantityApproved),
    totalApproved:
      payload.totalApproved === undefined
        ? lineItem.totalApproved
        : payload.totalApproved == null
          ? null
          : String(payload.totalApproved),
    unitApproved:
      payload.unitApproved === undefined
        ? lineItem.unitApproved
        : payload.unitApproved,
    unitPriceApproved:
      payload.unitPriceApproved === undefined
        ? lineItem.unitPriceApproved
        : payload.unitPriceApproved == null
          ? null
          : String(payload.unitPriceApproved),
  };
}

const approvedLineItemFields = [
  'quantityApproved',
  'unitApproved',
  'unitPriceApproved',
  'totalApproved',
] as const;

type InvoiceLineItem = InvoiceDetail['lineItems'][number];
/** Identifies line saves in flight, so other controls can wait for them. */
export const MUTATION_KEY_UPDATE_LINE_ITEM_APPROVED = [
  'invoice',
  'line-item-approved',
] as const;

function rollbackOptimisticLineItem(
  currentLineItem: InvoiceLineItem,
  previousLineItem: InvoiceLineItem,
  optimisticLineItem: InvoiceLineItem,
) {
  const rolledBackLineItem = { ...currentLineItem };

  for (const field of approvedLineItemFields) {
    if (Object.is(currentLineItem[field], optimisticLineItem[field])) {
      rolledBackLineItem[field] = previousLineItem[field] ?? null;
    }
  }

  return rolledBackLineItem;
}

type UpdateLineItemApprovedMutationContext = {
  callerResult: unknown;
  optimisticLineItem?: InvoiceLineItem;
  previousLineItem?: InvoiceLineItem;
};

/**
 * React Query mutation for updating approved invoice line-item values.
 *
 * @param options - Optional mutation configuration.
 * @returns Mutation state and methods.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/update_line_item_approved_invoices__invoice_id__line_items__line_item_id__approved_patch
 */
export function useMutationUpdateLineItemApproved(
  options?: UpdateLineItemApprovedMutationOptions,
) {
  const queryClient = useQueryClient();
  const { onError, onMutate, onSettled, ...mutationOptions } = options ?? {};

  // oxlint-disable sort-keys -- TanStack Query's mutation-property-order rule requires onMutate before onError.
  return useMutation<
    Awaited<ReturnType<typeof updateLineItemApproved>>,
    Error,
    UpdateLineItemApprovedVariables,
    UpdateLineItemApprovedMutationContext
  >({
    ...mutationOptions,
    mutationKey: MUTATION_KEY_UPDATE_LINE_ITEM_APPROVED,
    mutationFn: (variables: UpdateLineItemApprovedVariables) =>
      updateLineItemApproved(variables),
    async onMutate(variables, context) {
      const queryKey = queryKeysInvoice.getDetail(variables.invoiceId);

      await queryClient.cancelQueries({ queryKey });

      const previousInvoice = queryClient.getQueryData<InvoiceDetail>(queryKey);
      const previousLineItem = previousInvoice?.lineItems.find(
        (lineItem) => lineItem.id === variables.lineItemId,
      );
      const optimisticLineItem = previousLineItem
        ? getOptimisticLineItem(previousLineItem, variables.payload)
        : undefined;

      if (previousInvoice) {
        queryClient.setQueryData<InvoiceDetail>(queryKey, {
          ...previousInvoice,
          lineItems: previousInvoice.lineItems.map((lineItem) =>
            lineItem.id === variables.lineItemId
              ? (optimisticLineItem ?? lineItem)
              : lineItem,
          ),
        });
      }

      const callerResult = await onMutate?.(variables, context);
      return { callerResult, optimisticLineItem, previousLineItem };
    },
    async onError(error, variables, onMutateResult, context) {
      const { optimisticLineItem, previousLineItem } = onMutateResult ?? {};
      const queryKey = queryKeysInvoice.getDetail(variables.invoiceId);

      if (optimisticLineItem && previousLineItem) {
        queryClient.setQueryData<InvoiceDetail>(queryKey, (invoice) => {
          if (!invoice) {
            return invoice;
          }

          return {
            ...invoice,
            lineItems: invoice.lineItems.map((lineItem) =>
              lineItem.id === variables.lineItemId
                ? rollbackOptimisticLineItem(
                    lineItem,
                    previousLineItem,
                    optimisticLineItem,
                  )
                : lineItem,
            ),
          };
        });
      }

      return onError?.(error, variables, onMutateResult?.callerResult, context);
    },
    onSettled(data, error, variables, onMutateResult, context) {
      const hasConcurrentInvoiceMutations =
        queryClient.isMutating({
          mutationKey: MUTATION_KEY_UPDATE_LINE_ITEM_APPROVED,
          predicate: (mutation) =>
            (
              mutation.state.variables as
                | UpdateLineItemApprovedVariables
                | undefined
            )?.invoiceId === variables.invoiceId,
        }) > 1;

      if (!hasConcurrentInvoiceMutations) {
        // Not awaited: the mutation stays pending until this callback resolves.
        void queryClient.invalidateQueries({
          queryKey: queryKeysInvoice.getDetail(variables.invoiceId),
        });
      }

      return onSettled?.(data, error, variables, onMutateResult, context);
    },
  });
}
